import React, { useEffect, useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import { useSystemMode } from "@/context/SystemModeContext";
import { unlockEgg } from "@/lib/utils";

const ParticleField = () => {
  const canvasRef = useRef(null);
  const { resolvedTheme } = useTheme();
  const { isOverdrive, isDebug } = useSystemMode();
  const fpsHistoryRef = useRef([]);
  const ipRef = useRef("Fetching...");
  const clientInfoRef = useRef(null);
const serverInfoRef = useRef(null);
  const mouseRef = useRef({ x: null, y: null });
  const mouse = mouseRef.current;

  // Debug refs
  const debugIntervalRef = useRef(null);
  const fpsRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const neuralUnlockedRef = useRef(false);
  const neuralStartTimeRef = useRef(null);
const neuralAccumulatedRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const isDark = resolvedTheme === "dark";

    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;

        this.alpha = isDark
          ? Math.random() * 0.5 + 0.1
          : Math.random() * 0.35 + 0.12;

        this.size = Math.random() * 1.5 + 0.5;

        this.color = isDark
          ? Math.random() > 0.5
            ? "34, 211, 238"
            : "139, 92, 246"
          : Math.random() > 0.5
          ? "59, 130, 246"
          : "100, 116, 139";
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        // Mouse attraction
        if (mouse.x !== null && mouse.y !== null) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          const influenceRadius = isOverdrive ? 180 : 120;

          if (dist < influenceRadius) {
            const force = (influenceRadius - dist) / influenceRadius;
            this.x += dx * force * 0.02;
            this.y += dy * force * 0.02;

             // 🔥 Track neural interaction time
    if (!neuralUnlockedRef.current) {
      const now = performance.now();

      if (neuralStartTimeRef.current === null) {
        neuralStartTimeRef.current = now;
      } else {
        neuralAccumulatedRef.current += now - neuralStartTimeRef.current;
        neuralStartTimeRef.current = now;

        if (neuralAccumulatedRef.current >= 3000) {
          neuralUnlockedRef.current = true;
          unlockEgg("neural");
        }
      }
    }
          }
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color}, ${this.alpha})`;
        ctx.fill();
      }
    }

    const init = () => {
      resize();
      const particleCount = isOverdrive ? 120 : 80;
      particles = Array.from({ length: particleCount }, () => new Particle());
    };

    const drawConnections = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 120) {
            const opacityMultiplier = isOverdrive
              ? 0.15
              : isDark
              ? 0.08
              : 0.07;

            ctx.beginPath();
            ctx.strokeStyle = `rgba(34, 211, 238, ${
              opacityMultiplier * (1 - dist / 120)
            })`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    const drawMouseGlow = () => {
      if (mouse.x === null || mouse.y === null) return;

      const radius = isOverdrive ? 140 : 90;
      const gradient = ctx.createRadialGradient(
        mouse.x,
        mouse.y,
        0,
        mouse.x,
        mouse.y,
        radius
      );

      const glowAlpha = isDark ? 0.08 : 0.05;

      gradient.addColorStop(0, `rgba(34, 211, 238, ${glowAlpha})`);
      gradient.addColorStop(0.4, `rgba(34, 211, 238, ${glowAlpha * 0.5})`);
      gradient.addColorStop(1, "rgba(34, 211, 238, 0)");

      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    };

    const animate = () => {
      const now = performance.now();

      fpsRef.current = Math.round(1000 / (now - lastTimeRef.current));
      lastTimeRef.current = now;

      // Keep last 40 samples
      fpsHistoryRef.current.push(fpsRef.current);
      if (fpsHistoryRef.current.length > 40) {
        fpsHistoryRef.current.shift();
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      drawMouseGlow();

      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      drawConnections();

      animId = requestAnimationFrame(animate);
    };

    // Mouse listeners
    const handleMouseMove = (e) => {
  const rect = canvas.getBoundingClientRect();
  mouseRef.current.x = e.clientX - rect.left;
  mouseRef.current.y = e.clientY - rect.top;
};

const handleMouseLeave = () => {
  mouseRef.current.x = null;
  mouseRef.current.y = null;
};

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("resize", resize);

    init();
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", resize);
    };
  }, [resolvedTheme, isOverdrive]);

  
useEffect(() => {
  const discovered = JSON.parse(localStorage.getItem("easter-eggs") || "[]");
  if (discovered.includes("neural")) {
    neuralUnlockedRef.current = true;
  }
}, []);

useEffect(() => {
  if (!isDebug) return;

  // Fetch client info only once
  if (!clientInfoRef.current) {
    fetch("/api/debug/client", {
  method: "POST",   // 👈 here
})
      .then(res => res.json())
      .then(data => {
        clientInfoRef.current = data;
      })
      .catch(() => {
        clientInfoRef.current = { ip: "Unavailable" };
      });
  }

  // Fetch server info only once
  if (!serverInfoRef.current) {
    fetch("/api/debug/server", {
  method: "POST",   // 👈 here
})
      .then(res => res.json())
      .then(data => {
        serverInfoRef.current = data;
      })
      .catch(() => {
        serverInfoRef.current = { error: true };
      });
  }

}, [isDebug]);

  // Separate Debug Effect (Clean & Safe)
  useEffect(() => {
    if (!isDebug) {
      if (debugIntervalRef.current) {
        clearInterval(debugIntervalRef.current);
        debugIntervalRef.current = null;
      }
      return;
    }

    const renderFPSGraph = () => {
  const values = fpsHistoryRef.current;
  if (values.length === 0) return "";

  const max = 120;
  const width = 100;
  const height = 30;

  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - (Math.min(v, max) / max) * height;
      return `${x},${y}`;
    })
    .join(" ");

  return `
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <polyline 
    fill="none"
    stroke="currentColor"
    stroke-width="1"
    points="${points}"
  />
</svg>
  `;
};

    const updateDebugPanel = () => {
  const panel = document.getElementById("debug-panel");
  if (!panel) return;

  const memory = performance.memory
    ? (performance.memory.usedJSHeapSize / 1048576).toFixed(1) + " MB"
    : "N/A";

    const client = clientInfoRef.current;
    const server = serverInfoRef.current;

    const uaFull = navigator.userAgent;
    const shortUA =
      uaFull.length > 20 ? uaFull.slice(0, 20) + "…" : uaFull;

      const mouseX =
    mouse.x !== null ? Math.round(mouse.x) : "-";
  const mouseY =
    mouse.y !== null ? Math.round(mouse.y) : "-";

  const graph = renderFPSGraph();

  panel.innerHTML = `
<div class="font-semibold">DEBUG MODE</div>
<div class="text-muted-foreground mb-2">──────────────</div>

<div class="flex items-center justify-between">
  <div>FPS: <span class="text-primary">${fpsRef.current}</span></div>
</div>

<div class="text-primary mb-2">${graph}</div>

<div>Memory: <span class="text-primary">${memory}</span></div>
<div>Mouse: <span class="text-primary">${mouseX}, ${mouseY}</span></div>
<div>Overdrive: <span class="text-primary">${isOverdrive ? "ON" : "OFF"}</span></div>
<div>Theme: <span class="text-primary">${resolvedTheme === 'dark' ? "Dark":"Light"}</span></div>
<div>Time: <span class="text-primary">${new Date().toLocaleTimeString()}</span></div>

<div class="mt-2 text-muted-foreground leading-relaxed">
<div class="font-semibold">CLIENT</div>
Viewport: ${window.innerWidth} × ${window.innerHeight}<br/>
Platform: ${navigator.platform}<br/>
UA: ${shortUA}<br/>
IP: ${client?.ip || "Loading..."}<br/>
Location: ${client?.city || "-"}, ${client?.country || "-"}<br/>
ISP: ${client?.isp || "-"}
</div>
<div class="mt-2 text-muted-foreground leading-relaxed">
<div class="font-semibold">SERVER</div>
Server: ${server?.server_host || "Loading..."}<br/>
Python: ${server?.python_version || "-"}<br/>
Platform: ${server?.platform || "-"}
</div>
  `;
};

    debugIntervalRef.current = setInterval(updateDebugPanel, 300);

    return () => {
      if (debugIntervalRef.current) {
        clearInterval(debugIntervalRef.current);
        debugIntervalRef.current = null;
      }
    };
  }, [isDebug, isOverdrive, resolvedTheme]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.9 }}
    />
  );
};

export default ParticleField;